import React, { FC, useEffect, useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Button,
  ConfirmModal,
  FormGenerator,
  ModalWrapper,
  ScriptValueDialog as NewScriptValueDialog,
} from '@microservices/wiskey-react-components'
import { CircularProgress, Grid, Typography } from '@mui/material'

import DropDownInputWithFitlers from '@components/DropDownInputWithFitlers/DropDownInputWithFitlers'
import { FilterDialog } from '@components/FilterDialog/FilterDialog'
import { ModalFormContainer } from '@components/ModalFormContainer'
import { ScriptValueDialog } from '@components/ScriptValueDialog'
import { TextSettings } from '@components/TextSettings'

import {
  BIND_TYPE,
  DROPDOWN_FILTER_TYPES,
  ENTITY,
  MODAL_TYPE,
  MODEL_TYPE,
  PREFILL_OPTIONS,
} from '@constants'
import {
  ActionJSScriptConfig,
  AutocompleteOption,
  ConfiguredColumn,
  Dictionary,
  EntityType,
  GETAction,
  GETShareColumn,
  ModalType,
  ObjectCommandDTO,
  ObjectFieldDTO,
  PayloadCreateViewColumn,
  ShareColumnType,
  Table,
} from '@types'

import { ModalRefPathPicker } from '@gantt/components/GanttCreateOrEdit/components/ModalRefPathPicker'

import { useAddColumnDialog } from './hooks/useAddColumnDialog'

export type AddColumnDialogType = {
  onCloseModal: (isDirty?: boolean) => void
  showDialog: boolean
  modalType: ModalType
  rawObjectFields?: ObjectFieldDTO[]
  columns: ConfiguredColumn[]
  currentRow?: GETShareColumn | null
  objectCode: string
  objectCommands?: ObjectCommandDTO[]
  bindingValuesColumns: Dictionary[] | undefined
  entityCode: string
  currentEntity?: EntityType
  onSetModalProperty?: (type: ShareColumnType) => void
  entityFormCode: string
  onCreateColumn: (data: PayloadCreateViewColumn) => Promise<ConfiguredColumn>
  onUpdateColumn: (data: PayloadCreateViewColumn) => Promise<ConfiguredColumn>
  type?: MODEL_TYPE
  actionTable: Table<GETAction>
  isAllowPinningAllColumns?: boolean
  entityType: ENTITY
  loading?: boolean
  jsJSXHint?: string
  isUseNewScriptDialog?: boolean
  scriptConfig: ActionJSScriptConfig
}

export const AddColumnDialog: FC<AddColumnDialogType> = props => {
  const { t } = useTranslation()

  const {
    onCloseModal,
    showDialog,
    modalType,
    rawObjectFields,
    type,
    loading,
    entityType,
    jsJSXHint,
    scriptConfig,
    isUseNewScriptDialog,
  } = props

  const {
    isDirty,
    handleSave: onSave,
    isLoading,
    isShowScriptValueDialog,
    toggleOpenScriptValueDialog,
    methods,
    columnInputs,
    valueTypeInputs,
    viewObjectCode,
    sortInputs,
    formatDateInput,
    preFillTypeInput,
    preFillStaticValueInput,
    checkboxesInputs,
    columnHeaderSettingsRef,
    columnTextSettingsRef,
    initialColumnHeaderStyles,
    initialColumnTextStyles,
    watchUseParameters,
    watchBinding,
    filteredObjectFields,
    handleHeaderExternalDirty,
    handleTextExternalDirty,
    handleClickReset,
    handleResetParams,
    handleSetShowResetConfirmModal,
    isShowResetConfirmModal,
    dropwDownWindowFormInput,
    dropwDownListFormInput,
    handleAddFilter,
    handleDoubleClickChip,
    handleDeleteFilter,
    handleSubmitFilter,
    handleCloseFilterDialog,
    isShowFilterDialog,
    currentFilter,
    currentTypeFilter,
  } = useAddColumnDialog(props)

  const { setValue, watch } = methods
  const watchedBindType = watch('bindType')
  const watchedValue = watch('value')
  const watchedPathArray = watch('pathArray')
  const watchedObjectValue = watch('objectValue')
  const watchDisableEditable = watch('disableEditable')
  const watchPreFillSourceObject = watch('preFillSourceObject')
  const watchPreFillFilterObject = watch('preFillFilterObject')
  const watchPreFillType = watch('preFillType')
  const watchedDropDownListFilters = watch('dropDownListFilters')
  const watchDropdownList = watch('dropDownListCode')
  const watchedDropDownWindowFilters = watch('dropDownWindowFilters')
  const watchDropdownWindow = watch('dropDownWindowCode')
  const [initialValue, setInitialValue] = useState({
    pathArray: watchedPathArray,
    objectValue: watchedObjectValue,
  })

  useEffect(() => {
    if (watchedValue) {
      methods.resetField('dropDownListCode')
      methods.resetField('dropDownWindowCode')
    }
  }, [watchedValue, methods.resetField])

  const objectCode = useMemo(
    () => filteredObjectFields?.find(({ name }) => name === watchedValue)?.model,
    [filteredObjectFields, watchedValue]
  )

  const handleChangeSourceObject = (value: AutocompleteOption<string>) => {
    setValue('preFillSourceObject', value)
  }

  const handleChangeFilterObject = (value: AutocompleteOption<string>) => {
    setValue('preFillFilterObject', value)
  }

  const handleSave = () => {
    onSave(
      columnHeaderSettingsRef.current?.getTextParams(),
      columnTextSettingsRef.current?.getTextParams()
    )
  }

  const handleCloseScriptValueDialog = () => {
    toggleOpenScriptValueDialog(false)
  }

  const handleSaveScript = (value: string) => {
    setValue('value', value, { shouldDirty: true })
  }

  useEffect(() => {
    setInitialValue({
      pathArray: watchedPathArray,
      objectValue: watchedObjectValue,
    })
  }, [watchedObjectValue])

  return (
    <ModalWrapper
      disabledSave={loading || !isDirty}
      isShow={showDialog}
      title={`${t(`addDialog.${modalType}`)} Column`}
      containerStyle={{
        px: 0.5,
        py: 0.5,
        // borderRadius: 0
      }}
      onSave={handleSave}
      onClose={() => {
        onCloseModal(isDirty)
      }}
    >
      {isShowFilterDialog && (
        <FilterDialog
          filter={currentFilter}
          isShow={isShowFilterDialog}
          onClose={handleCloseFilterDialog}
          onSubmit={handleSubmitFilter}
        />
      )}
      {isLoading && modalType === MODAL_TYPE.EDIT ? (
        <Grid container alignItems={'center'} justifyContent={'center'} my={3}>
          <CircularProgress />
        </Grid>
      ) : (
        <ModalFormContainer maxHeight={600}>
          <ConfirmModal
            actionBtnText={t('parameters.resetModal.actionBtnText')}
            isShow={isShowResetConfirmModal}
            text={t('parameters.resetModal.text')}
            title={t('parameters.resetModal.title')}
            containerStyle={{
              px: 0.5,
              py: 0.5,
              // borderRadius: 0
            }}
            onClose={() => handleSetShowResetConfirmModal(false)}
            onConfirm={handleResetParams}
          />
          <FormProvider {...methods}>
            {isShowScriptValueDialog &&
              (isUseNewScriptDialog ? (
                <NewScriptValueDialog
                  {...scriptConfig}
                  isShow={isShowScriptValueDialog}
                  language={watchedBindType === BIND_TYPE.JSX ? 'jsx' : 'js'}
                  value={watchedValue as string}
                  onClose={handleCloseScriptValueDialog}
                  onSave={handleSaveScript}
                />
              ) : (
                <ScriptValueDialog
                  windowBoundaries
                  hint={jsJSXHint}
                  isJsx={watchedBindType === BIND_TYPE.JSX}
                  isShow={isShowScriptValueDialog}
                  modalType={modalType}
                  objectFields={rawObjectFields}
                  value={watchedValue as string}
                  onClose={handleCloseScriptValueDialog}
                  onSave={handleSaveScript}
                />
              ))}
            <form>
              <FormGenerator inputs={columnInputs} mainGridSpacing={0} />
              <FormGenerator inputs={valueTypeInputs} mainGridSpacing={0} />
              {watchedBindType === BIND_TYPE.FIELD && Boolean(objectCode) && (
                <Grid container>
                  <Grid item>
                    <Typography
                      sx={{
                        fontSize: '1rem',
                        minWidth: 120,
                        mt: 1,
                        color: '#616161',
                      }}
                    >
                      {t('label.displayField')}
                    </Typography>
                  </Grid>
                  <Grid item style={{ flex: 1 }}>
                    <ModalRefPathPicker
                      hasClearInInput
                      isRequired
                      controllerNamePathArray={`pathArray`}
                      controllerNamePathStr={`objectValue`}
                      currentValue={initialValue}
                      embeddedObjectPickerControllerName={`pathArray`}
                      name={`objectValue`}
                      object={objectCode}
                      placeHolder={t('placeholder.displayField')}
                    />
                  </Grid>
                </Grid>
              )}
              <FormGenerator inputs={formatDateInput} mainGridSpacing={0} />
              {watchedBindType !== BIND_TYPE.ACTION &&
                type !== MODEL_TYPE.TIMELINE_OBJECT_X_COLUMN && (
                  <FormGenerator inputs={sortInputs} mainGridSpacing={0} />
                )}
              <FormGenerator inputs={checkboxesInputs} mainGridSpacing={0} />

              {!watchDisableEditable && watchedObjectValue && (
                <>
                  <DropDownInputWithFitlers
                    filters={watchedDropDownListFilters || []}
                    inputs={[dropwDownListFormInput]}
                    watchInput={watchDropdownList}
                    onAddFilter={() => handleAddFilter(DROPDOWN_FILTER_TYPES.LIST)}
                    onDeleteFilter={filter =>
                      handleDeleteFilter(filter, DROPDOWN_FILTER_TYPES.LIST)
                    }
                    onDoubleClickFilter={filter =>
                      handleDoubleClickChip(filter, DROPDOWN_FILTER_TYPES.LIST)
                    }
                  />
                  <DropDownInputWithFitlers
                    filters={watchedDropDownWindowFilters}
                    inputs={[dropwDownWindowFormInput]}
                    watchInput={watchDropdownWindow}
                    onAddFilter={() => handleAddFilter(DROPDOWN_FILTER_TYPES.WINDOW)}
                    onDeleteFilter={filter =>
                      handleDeleteFilter(filter, DROPDOWN_FILTER_TYPES.WINDOW)
                    }
                    onDoubleClickFilter={filter =>
                      handleDoubleClickChip(filter, DROPDOWN_FILTER_TYPES.WINDOW)
                    }
                  />
                </>
              )}
              {watchBinding === BIND_TYPE.FIELD && entityType !== ENTITY.DROP_DOWN_ENTITY && (
                <FormGenerator inputs={preFillTypeInput} mainGridSpacing={0} />
              )}

              {watchPreFillType?.option === PREFILL_OPTIONS.DYNAMIC && (
                <ModalRefPathPicker
                  hasClearInInput
                  isRequired
                  embeddedObjectPickerControllerName='preFillLink'
                  isDisabled={false}
                  label={t('label.preFillSourceObject')}
                  name={`preFillLinkStr`}
                  object={watchPreFillSourceObject}
                  pickerName='preFillLink'
                  onChangeRootObject={handleChangeSourceObject}
                />
              )}

              {watchPreFillType?.option === PREFILL_OPTIONS.STATIC && (
                <>
                  <ModalRefPathPicker
                    hasClearInInput
                    isRequired
                    embeddedObjectPickerControllerName='preFillLink'
                    isDisabled={false}
                    label={t('label.preFillSourceObject')}
                    name={`preFillLinkStr`}
                    object={watchPreFillSourceObject}
                    pickerName='preFillLink'
                    onChangeRootObject={handleChangeSourceObject}
                  />
                  <ModalRefPathPicker
                    hasClearInInput
                    isRequired
                    embeddedObjectPickerControllerName='preFillFilterName'
                    isDisabled={false}
                    label={t('label.preFillFilterField')}
                    name={`preFillFilterNameStr`}
                    object={watchPreFillFilterObject}
                    pickerName='preFillFilterName'
                    onChangeRootObject={handleChangeFilterObject}
                  />
                  <FormGenerator inputs={preFillStaticValueInput} mainGridSpacing={0} />
                </>
              )}

              <Typography variant='h6'>{t('addDialog.parameters')}</Typography>
              <TextSettings
                ref={columnHeaderSettingsRef}
                backgroundSettings={false}
                borderSettings={false}
                initialTextState={initialColumnHeaderStyles ? initialColumnHeaderStyles : undefined}
                isEdit={watchUseParameters}
                labelPlacement='top'
                labelText={t('parameters.visual.columnHeader')}
                onSetExternalDirty={handleHeaderExternalDirty}
              />
              <TextSettings
                ref={columnTextSettingsRef}
                initialTextState={initialColumnTextStyles ? initialColumnTextStyles : undefined}
                isEdit={watchUseParameters}
                labelPlacement='top'
                labelText={t('parameters.visual.columnText')}
                onSetExternalDirty={handleTextExternalDirty}
              />
              <Grid container item justifyContent={'flex-end'} mt={0.5}>
                <Button variant='outlined' onClick={handleClickReset}>
                  {t('parameters.resetButton')}
                </Button>
              </Grid>
            </form>
          </FormProvider>
        </ModalFormContainer>
      )}
    </ModalWrapper>
  )
}
